import store from '@/store/store'
import { SignInManager } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import {
  resolveAIModelsAccessDecision,
  type AIModelsAccessDecision,
} from '@/services/ai/aiAccessPolicy'

/**
 * Resolves the AI Models access decision, but first forces a one-shot profile
 * refresh when local state reports a premium user as out of credits.
 *
 * Handles a known race: after checkout the subscription tier flips to premium
 * quickly while `remaining_credits` can still be stale (`0`) — or missing on a
 * reload from older cached `UserInfoStorage` — until the profile refresh
 * completes. Without this, a paying user is blocked by stale/missing credits on
 * immediate entry to a credit-gated flow even though the backend would report
 * available credits. The backend stays authoritative for decrementing credits.
 *
 * Canonical implementation shared by all credit-gated AI Models entry points
 * (AI Models / AI Fashion and Photoshoots). It is kept out of the pure
 * `aiAccessPolicy` module so that module's many importers don't pull in the
 * heavy `store` / `SignInManager` dependency graph.
 */
export async function resolveAIModelsAccessDecisionWithForcedPremiumCreditsRefresh(): Promise<AIModelsAccessDecision> {
  const initialDecision = resolveAIModelsAccessDecision(store.getState())
  if (initialDecision.reason !== 'premiumOutOfCredits') {
    return initialDecision
  }

  const isFresh = await SignInManager.getInstance().ensureAIModelsCreditsFresh(0)
  if (!isFresh) return initialDecision

  return resolveAIModelsAccessDecision(store.getState())
}
